import { useEffect } from "react";
import PropTypes from "prop-types";
import { navigate } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import { useSubscriptionPopup } from "@context/SubscriptionPopupProvider";
import useResponsiveWithHydrationFix from "@hooks/useResponsiveWithHydrationFix";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { isBrowser } from "@helpers/browser";
import { cn } from "@helpers/general";
import { ALButtonV2 } from "@components/ALComponents";
import IconClose from "@svgs/close-br.svg";

import "@components/subscriptionPopUp/styles.scss";

const FREE_RETURNS_PERK = "Free returns";

const SUBSCRIPTION_TEXT = {
  title: "The Most Affordable Way to Shop",
  description:
    "Join our AL Luxe membership where $49.99/month becomes store credit—plus enjoy savings & perks with every purchase.",
  perks: [
    "Up to 25% off every order",
    "5% off all solid gold purchases",
    "$40 birthday gift card",
    FREE_RETURNS_PERK,
    "Early access to sales and new drops",
    "Pause, skip, or cancel at anytime",
  ],
  buttonText: "Learn More",
  disclaimer: "Highest savings automatically applied in cart with AL Luxe membership.",
};

function SubscriptionPopUp({ alwaysMounted = false }) {
  const { subscriptionPopupOpen, setSubscriptionPopupOpen, isFinalSale } = useSubscriptionPopup();

  // Final Sale products can't be returned
  const perks = isFinalSale
    ? SUBSCRIPTION_TEXT.perks.filter((perk) => perk !== FREE_RETURNS_PERK)
    : SUBSCRIPTION_TEXT.perks;

  const handleClose = () => {
    setSubscriptionPopupOpen(false);
  };

  const handleLearnMore = () => {
    handleClose();
    navigate("/welcome-to-ana-luisa-luxe");
  };

  // When you leave PDP, it sets subscriptionPopupOpen to false
  useEffect(() => {
    if (!alwaysMounted) return undefined;
    return () => setSubscriptionPopupOpen(false);
  }, [alwaysMounted, setSubscriptionPopupOpen]);

  // When the popup is open, we want to prevent scrolling on the page behind it. We also want to restore the original overflow style when the popup is closed or when the component unmounts.
  useEffect(() => {
    if (!isBrowser || !subscriptionPopupOpen) return undefined;

    const { overflow } = document.documentElement.style;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = overflow;
    };
  }, [subscriptionPopupOpen]);

  const popUpImage = useResponsiveWithHydrationFix(
    <StaticImage
      src="../images/subscription/luxe-logo-popup.png"
      alt="Mobile Subscription PopUp Logo"
      width={100}
      height={50}
    />,
    <StaticImage
      src="../images/subscription/luxe-logo-popup.png"
      alt="Desktop Subscription PopUp Logo"
      width={120}
      height={59}
    />
  );

  if (!alwaysMounted && !subscriptionPopupOpen) {
    return null;
  }

  return (
    <div
      className={cn("popup_subscription__overlay", {
        "popup_subscription__overlay--closed": !subscriptionPopupOpen,
      })}
    >
      <div
        className="popup_subscription__backdrop"
        data-testid="subscription-popup-backdrop"
        onClick={handleClose}
      />
      <div className="popup_subscription__content">
        <button
          type="button"
          aria-label="Close popup"
          data-testid="close-button"
          className="cursor-pointer absolute right-4 top-4 md:right-[20px] md:top-[20px] z-10 bg-transparent border-0 p-0"
          onClick={handleClose}
        >
          <IconClose width="15" height="15" fill="none" />
        </button>
        <div className="relative bg-white rounded-[6px] desktop:rounded-[12px] p-[30px] desktop:p-[40px] border border-black">
          <div className="flex flex-col items-center">
            {popUpImage}
            <h2 className="title-m font-bold desktop:title-s mt-[25px] desktop:mt-[30px] mb-[10px]">
              {SUBSCRIPTION_TEXT.title}
            </h2>
            <p className="typo-body mb-[25px] desktop:mb-[30px]">{SUBSCRIPTION_TEXT.description}</p>
            <ul className="text-left list-disc list-inside typo-body space-y-[10px] pl-[10px] mb-[25px] desktop:mb-[30px] w-full">
              {perks.map((perk) => (
                <li key={perk}>{perk}</li>
              ))}
            </ul>
            <ALButtonV2
              type="button"
              variant="primary"
              size="large"
              fullWidth
              onClick={handleLearnMore}
            >
              {SUBSCRIPTION_TEXT.buttonText}
            </ALButtonV2>
            <p className="typo-micro mt-[8px] desktop:mt-[10px]">{SUBSCRIPTION_TEXT.disclaimer}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

SubscriptionPopUp.propTypes = {
  alwaysMounted: PropTypes.bool,
};

export default withALErrorBoundary({
  name: "SubscriptionPopUp",
  priority: "P3",
})(SubscriptionPopUp);
